import React, { useState } from "react";
import clsx from "clsx";
import { useEffect } from "react";
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridRow,
  GridRowParams,
} from "@mui/x-data-grid";
import { Box, Button, Stack, TextField } from "@mui/material";
import { axiosInstance } from "../../../utils/networking";
import LargeScreenNotice from "../../common/LargeScreenNotice";
import { useViewport } from "../../../utils/useViewport";

// Phones show the company name only.
const PHONE_COLUMNS: GridColumnVisibilityModel = {
  contact: false,
  city: false,
};
const ALL_COLUMNS: GridColumnVisibilityModel = {};

const ExploreArchiveFirm = () => {
  const [hisFirm, setHisFirm] = useState<any>([]);
  const [displayedFirms, setDisplayedFirms] = useState<any>([]);
  const [selectedRoww, setSelectedRoww] = useState([]);
  const [search, setSearch] = useState("");
  const { isPhone } = useViewport();
  // Crossing the phone breakpoint resets the columns; in between, the
  // column menu can still hide and show them.
  const [columnVisibility, setColumnVisibility] = useState(() =>
    isPhone ? PHONE_COLUMNS : ALL_COLUMNS,
  );
  useEffect(() => {
    setColumnVisibility(isPhone ? PHONE_COLUMNS : ALL_COLUMNS);
  }, [isPhone]);
  useEffect(() => {
    axiosInstance.get(`/firms/history`).then((res) => {
      // console.log(res.data.firms);
      setHisFirm(res.data.firms);
      setDisplayedFirms(res.data.firms);
    });
  }, []);

  function CustomRow(props: any) {
    const { className, index, ...other } = props;

    return (
      <GridRow
        index={index}
        className={clsx(className, index % 2 === 0 ? "odd" : "even")}
        {...other}
      />
    );
  }

  const columns: GridColDef[] = [
    {
      flex: 1,
      field: "title",
      headerName: "Company Name",
      headerAlign: "center",
      minWidth: 200,
      renderCell: (cellValues) => {
        // Phones hide the Contact column, so a firm with no company name
        // shows its contact instead (muted) rather than a blank row.
        const showContact = isPhone && !cellValues.row.title;
        return (
          <div className="flex flex-col items-start">
            <div style={{ whiteSpace: "pre-wrap" }}>
              {showContact ? (
                <Box
                  component="p"
                  sx={{ color: "text.secondary", fontSize: "18px" }}
                >
                  {cellValues.row.contact}
                </Box>
              ) : (
                <p
                  style={{
                    color: "#9E1F63FF",
                    fontSize: "22px",
                    textAlign: "left",
                  }}
                >
                  {cellValues.row.title}
                </p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      flex: 1,
      field: "contact",
      headerName: "Contact",
      headerAlign: "center",
      minWidth: 250,
      renderCell: (cellValues) => {
        return (
          <>
            <p style={{ fontSize: "18px" }}>{cellValues.row.contact}</p>
          </>
        );
      },
    },
    {
      flex: 1,
      field: "city",
      headerName: "City",
      headerAlign: "center",
      renderCell: (cellValues) => {
        return (
          <>
            <p style={{ paddingLeft: "20px", fontSize: "18px" }}>
              {cellValues.row.city}
            </p>
          </>
        );
      },
    },
  ];

  let rows: any[] = [];
  let counter: number = 0;
  for (let firm of displayedFirms) {
    let descriptionText: string = "";
    if (firm.hasOwnProperty("capabilityNarrative")) {
      descriptionText = descriptionText + firm["capabilityNarrative"] + " ";
    }
    if (firm.hasOwnProperty("salesforceDescription")) {
      if (
        firm["salesforceDescription"] != null &&
        firm["salesforceDescription"].hasOwnProperty("descriptionText")
      ) {
        descriptionText =
          descriptionText + firm["salesforceDescription"]["descriptionText"];
      }
    }
    rows.push({
      id: counter,
      title: firm["Company Name"],
      contact: firm["Contact"],
      city: firm["City"],
      firm_id: firm["_id"],
    });
    counter += 1;
  }

  const handleSearchSubmit = () => {
    axiosInstance
      .post(`/firms/his_search`, {
        term: search,
      })
      .then((res) => {
        // console.log(res.data);
        setDisplayedFirms(res.data.firms);
      });
  };

  return (
    <>
      <LargeScreenNotice pageKey="archived-cases" />
      <Stack
        direction={"row"}
        spacing={2}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          width: { xs: "100%", sm: "50%" },
          marginBottom: "20px",
        }}
      >
        <TextField
          sx={{ width: "100%" }}
          label="Search Company..."
          variant="outlined"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        <Button
          style={{ backgroundColor: "#9e1f63", color: "white" }}
          variant="contained"
          onClick={handleSearchSubmit}
        >
          Search
        </Button>
        <Button
          style={{ backgroundColor: "#9e1f63", color: "white" }}
          variant="contained"
          onClick={() => {
            setSearch("");
            setDisplayedFirms(hisFirm);
          }}
        >
          Reset
        </Button>
      </Stack>
      <Box
        style={{
          height: 800,
        }}
        sx={{
          height: 400,
          width: 1,
          "& .odd": {
            bgcolor: "#e8e8e8",
          },
          "& .even": {
            bgcolor: "#f8f7f7",
          },
          ".MuiDataGrid-columnHeaders": {
            fontSize: "22px",
            bgcolor: "#0F95B3",
            color: "white",
            textAlign: "left",
          },
          ".MuiSvgIcon-root": {},
        }}
      >
        <DataGrid
          sx={{
            "& .MuiDataGrid-row:hover": {
              cursor: "pointer",
            },
          }}
          rows={rows}
          columns={columns}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={setColumnVisibility}
          rowHeight={100}
          disableRowSelectionOnClick
          components={{ Row: CustomRow }}
          onRowClick={(row: any) => {
            window.open(
              `/dashboard/archivedcases/${row["row"]["firm_id"]}`,
              "_blank"
            );
          }}
          rowSelectionModel={selectedRoww}
          onRowSelectionModelChange={(row: any) => {
            setSelectedRoww(row);
          }}
        />
      </Box>
    </>
  );
};

export default ExploreArchiveFirm;
